import { useCallback, useEffect, useRef } from 'react'
import { useAvatarStore } from '@/shared/store/avatar-store'
import { useRealtimeAgentStore } from '@/shared/store/realtime-agent'
import { useToastStore } from '@/layouts/custom-avatar-page/toast/model/toast-store'
import i18n from '@/shared/lib/i18n'
import type { MessageMode, LanguageCode } from '@/entities/realtime-agent/types/websocket-messages'

interface RealtimeMessagePayload {
  mode: MessageMode
  data: string
  language: LanguageCode
}

interface UseMessageHandlerParams {
  currentChatMode: 'voice' | 'chat'
  isAudioPlaying: boolean
  sendTextChatMessage: (message: string) => void
  sendRealtimeMessage: (payload: RealtimeMessagePayload) => boolean
  pauseAudio?: () => void
  // Backend Realtime API (웹 검색 지원)
  sendBackendRealtimeMessage?: (message: string) => void
}

/**
 * 메시지 전송 핸들러 Hook
 *
 * 책임:
 * - 채팅/음성 모드에 따른 메시지 전송 분기
 * - 오디오 재생 중단 처리
 * - 애니메이션 상태 변경
 * - Backend Realtime API (웹 검색) 지원
 */
export const useMessageHandler = ({
  currentChatMode,
  isAudioPlaying,
  sendTextChatMessage,
  sendRealtimeMessage,
  pauseAudio,
  sendBackendRealtimeMessage,
}: UseMessageHandlerParams) => {
  const setCurrentAnimation = useAvatarStore(state => state.setCurrentAnimation)
  const addUserMessage = useRealtimeAgentStore(state => state.addUserMessage)
  const chatBackend = useRealtimeAgentStore(state => state.chatBackend)
  const showToast = useToastStore(state => state.showToast)

  // 🌐 언어 동기화 - i18n 이벤트 직접 구독 (즉시 반영)
  const currentLanguageRef = useRef(i18n.language as LanguageCode)

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      currentLanguageRef.current = lng as LanguageCode
      console.log('🌐 MessageHandler: languageChanged 이벤트 수신 (즉시 반영):', lng)
    }

    // 초기값 설정
    currentLanguageRef.current = i18n.language as LanguageCode
    console.log('🌐 MessageHandler: 초기 언어 설정:', i18n.language)

    // 이벤트 구독
    i18n.on('languageChanged', handleLanguageChange)

    return () => {
      i18n.off('languageChanged', handleLanguageChange)
    }
  }, [])

  const handleSendMessage = useCallback((message: string) => {
    if (!message.trim()) return

    if (currentChatMode === 'chat') {
      // 채팅 모드: chatBackend에 따라 분기
      if (chatBackend === 'backend_realtime' && sendBackendRealtimeMessage) {
        // Backend Realtime API 사용 (웹 검색 지원)
        // 내부에서 addUserMessage 호출됨
        console.log('🔍 [MESSAGE-HANDLER] Backend Realtime API로 메시지 전송')
        sendBackendRealtimeMessage(message)
      } else {
        // 기존 텍스트 채팅 사용 (내부에서 addUserMessage 호출됨)
        sendTextChatMessage(message)
      }
    } else if (currentChatMode === 'voice') {
      // 음성 모드: WebSocket을 통해 텍스트 메시지 전송

      // 사용자 메시지를 즉시 메시지 히스토리에 추가
      addUserMessage(message)

      // 사용자 메시지를 STT 토스트로 표시 (아바타 화면에서 피드백)
      showToast({
        type: 'stt',
        message,
        position: 'bottom',
        duration: 3000,
      })

      // 텍스트 입력 시 애니메이션 상태 변경
      setCurrentAnimation("toss")

      // 아바타가 현재 오디오 재생 중이면 즉시 중단
      if (isAudioPlaying) {
        console.log('🛑 오디오 재생 중단')
        pauseAudio?.() // 즉시 오디오 중단
      }

      // WebSocket으로 텍스트 메시지 전송 (음성 응답 받음)
      sendRealtimeMessage({
        mode: 'text',
        data: message,
        language: currentLanguageRef.current
      })
    }
  }, [
    currentChatMode,
    chatBackend,
    isAudioPlaying,
    sendTextChatMessage,
    sendRealtimeMessage,
    sendBackendRealtimeMessage,
    pauseAudio,
    setCurrentAnimation,
    addUserMessage,
    showToast
  ])

  return {
    handleSendMessage
  }
}
